// Clearing and restoring what is "on this map", through the same store actions the Maps
// sheet uses, so layers come off (and back on) exactly as if toggled there.
//
// Deliberately touches only four things: POI types, overlays (incl. weather, heatmap,
// coverage, official hikes, land access, cycling), the trail hazard markers (on/off, the
// same switch as the Maps sheet's More → Hazards row) and the walker's own hike lines
// (incl. the Hike lists overlay — on/off only, never its list selection or styles). It
// never reaches the base map style, 3D, waypoints, a measurement, the camera, or the
// selected hike/route — none of the actions below do either.

import {
  activeHikeSources,
  HAZARD_GROUP_KEYS,
  HIKE_GROUP_KEYS,
  OnThisMapGroup,
  OnThisMapState,
  OVERLAY_GROUP_KEYS,
  OVERLAY_TOGGLES,
  OverlayFlags,
  SwitchedOnAt,
  WeatherLayer,
} from '../../../utils/onThisMap';
import { sharedHazardLayer } from '../../../utils/trailHazards/hazardLayerController';

/** The parts of a Vuex store this needs, so tests can pass a fake. */
export interface OnThisMapStore {
  state: any;
  dispatch: (type: string, payload?: unknown) => unknown;
  commit: (type: string, payload?: unknown) => void;
}

export interface OnThisMapSnapshot {
  groups: OnThisMapGroup[];
  poiTypes: string[];
  poiSwitchedOnAt: SwitchedOnAt;
  overlays: OverlayFlags;
  activeSources: string[];
  hikeListsActive: boolean;
  hazardsActive: boolean;
  switchedOnAt: SwitchedOnAt;
}

export function readOnThisMapState(store: OnThisMapStore): OnThisMapState {
  const pois = store.state.mapDataPOIs ?? {};
  const overlays = store.state.mapDataOverlays ?? {};
  return {
    poiTypes: pois.activatedPoiTypes ?? [],
    poiSwitchedOnAt: pois.poiSwitchedOnAt ?? {},
    overlays: overlayFlags(overlays),
    activeSources: overlays.activeSources ?? [],
    hikeListsActive: !!overlays.hikeListsActive,
    // Hazard markers are drawn for a signed-in walker only; for anyone else the switch
    // shows nothing, so it isn't "on this map" (and the More view has no Hazards row).
    hazardsActive: !!overlays.hazardsActive && sharedHazardLayer().isAvailable(),
    switchedOnAt: overlays.switchedOnAt ?? {},
  };
}

function overlayFlags(overlays: any): OverlayFlags {
  const flags = {} as OverlayFlags;
  OVERLAY_TOGGLES.forEach(({ flag }) => { flags[flag] = !!overlays[flag]; });
  const weather: WeatherLayer = overlays.activeWeatherLayer ?? { name: null, type: null };
  flags.activeWeatherLayer = { name: weather.name ?? null, type: weather.type ?? null };
  return flags;
}

/** A deep copy of the current selection, kept in memory for Undo. */
export function takeSnapshot(store: OnThisMapStore, groups: OnThisMapGroup[]): OnThisMapSnapshot {
  const current = readOnThisMapState(store);
  return {
    groups: [...groups],
    poiTypes: [...current.poiTypes],
    poiSwitchedOnAt: { ...current.poiSwitchedOnAt },
    overlays: { ...current.overlays, activeWeatherLayer: { ...current.overlays.activeWeatherLayer } },
    activeSources: [...current.activeSources],
    hikeListsActive: !!current.hikeListsActive,
    hazardsActive: !!current.hazardsActive,
    switchedOnAt: { ...current.switchedOnAt },
  };
}

/** Switches off every item in `groups`. Persists immediately (the mutations write storage). */
export async function clearGroups(store: OnThisMapStore, groups: OnThisMapGroup[]): Promise<OnThisMapSnapshot> {
  const snapshot = takeSnapshot(store, groups);
  const pending: unknown[] = [];

  if (groups.includes('pois') && snapshot.poiTypes.length > 0) {
    pending.push(store.dispatch('mapDataPOIs/clearAllPoi'));
  }

  if (groups.includes('overlays')) {
    OVERLAY_TOGGLES.forEach(({ flag, toggle }) => {
      if (snapshot.overlays[flag]) pending.push(store.dispatch(`mapDataOverlays/${toggle}`));
    });
    const weather = snapshot.overlays.activeWeatherLayer;
    // Toggling the active weather layer switches it off.
    if (weather.name && weather.type) {
      pending.push(store.dispatch('mapDataOverlays/toggleWeatherOverlay', { ...weather }));
    }
  }

  // Off through the store, like the other overlays: no hazards_toggled — that event is the
  // walker flipping the switch in the More view, not a bulk clear.
  if (groups.includes('hazards') && snapshot.hazardsActive) {
    pending.push(store.dispatch('mapDataOverlays/toggleHazardsOverlay', false));
  }

  if (groups.includes('hikes')) {
    activeHikeSources(snapshot.activeSources).forEach((src) => {
      pending.push(store.dispatch('mapDataOverlays/toggleTrail', src));
    });
    if (snapshot.hikeListsActive) pending.push(store.dispatch('mapDataOverlays/toggleHikeListsOverlay'));
  }

  await Promise.all(pending);
  return snapshot;
}

/**
 * Puts the cleared groups back exactly as the snapshot had them — including anything
 * switched on or off in those groups since, and the switched-on order of the chips.
 */
export async function restoreSnapshot(store: OnThisMapStore, snapshot: OnThisMapSnapshot): Promise<void> {
  const pending: unknown[] = [];
  const current = readOnThisMapState(store);

  if (snapshot.groups.includes('pois')) {
    store.commit('mapDataPOIs/SET_ACTIVATED_POI_TYPES', [...snapshot.poiTypes]);
    pending.push(store.dispatch('mapDataPOIs/updatePoiFilter'));
  }

  if (snapshot.groups.includes('overlays')) {
    OVERLAY_TOGGLES.forEach(({ flag, toggle }) => {
      if (current.overlays[flag] !== snapshot.overlays[flag]) {
        pending.push(store.dispatch(`mapDataOverlays/${toggle}`));
      }
    });
    const was = snapshot.overlays.activeWeatherLayer;
    const now = current.overlays.activeWeatherLayer;
    if (was.name !== now.name) {
      if (was.name && was.type) {
        // Switching to the snapshot's layer also removes whatever is on now.
        pending.push(store.dispatch('mapDataOverlays/toggleWeatherOverlay', { ...was }));
      } else if (now.name && now.type) {
        pending.push(store.dispatch('mapDataOverlays/toggleWeatherOverlay', { ...now }));
      }
    }
  }

  if (snapshot.groups.includes('hazards') && !!current.hazardsActive !== snapshot.hazardsActive) {
    pending.push(store.dispatch('mapDataOverlays/toggleHazardsOverlay', snapshot.hazardsActive));
  }

  if (snapshot.groups.includes('hikes')) {
    const wanted = activeHikeSources(snapshot.activeSources);
    const have = activeHikeSources(current.activeSources);
    have.filter((src) => !wanted.includes(src))
      .forEach((src) => pending.push(store.dispatch('mapDataOverlays/toggleTrail', src)));
    wanted.filter((src) => !have.includes(src))
      .forEach((src) => pending.push(store.dispatch('mapDataOverlays/toggleTrail', src)));
    if (!!current.hikeListsActive !== snapshot.hikeListsActive) {
      pending.push(store.dispatch('mapDataOverlays/toggleHikeListsOverlay'));
    }
  }

  await Promise.all(pending);

  // The toggles above stamp everything they switch on with "now"; put the original
  // stamps (and, for hike lines, the original order) back.
  if (snapshot.groups.includes('pois')) {
    store.commit('mapDataPOIs/RESTORE_POI_SWITCHED_ON_AT', { ...snapshot.poiSwitchedOnAt });
  }
  if (snapshot.groups.includes('hikes')) {
    const others = (store.state.mapDataOverlays?.activeSources ?? [])
      .filter((src: string) => !activeHikeSources([src]).length);
    store.commit('mapDataOverlays/SET_ACTIVE_SOURCES', [...activeHikeSources(snapshot.activeSources), ...others]);
  }
  const keys = [
    ...(snapshot.groups.includes('overlays') ? OVERLAY_GROUP_KEYS : []),
    ...(snapshot.groups.includes('hazards') ? HAZARD_GROUP_KEYS : []),
    ...(snapshot.groups.includes('hikes') ? HIKE_GROUP_KEYS : []),
  ];
  if (keys.length > 0) {
    store.commit('mapDataOverlays/RESTORE_SWITCHED_ON_AT', { keys, from: { ...snapshot.switchedOnAt } });
  }
}
